/**
 * Run-scoped control tools for the multi-hop executor agent.
 *
 * These tools mutate the executor's live `ExecutorPlanState` — they
 * exist only for the duration of a single agent run and close over the
 * mutable plan state created at run start. Unlike read tools (which are
 * stateless and registered globally), these are constructed per-run via
 * `createExecutorToolRuntime(ctx)`.
 *
 * The returned `toolDefs` are passed to `buildToolDefinitionsForProvider`
 * via the `extraTools` param so they appear in the model's tool list.
 * The returned `executors` map is used by the agent's dispatch wrapper
 * before falling back to the global `executeToolCallDetailed`.
 */

import {
  CHART_CAPABILITY_TOOLS,
  executeChartCapabilityTool,
  type ChartCapabilityToolDef,
} from "./chartCapabilityTools";
import { setStepResultLine } from "./executorAccumulators";
import type { ApplyModificationsResult } from "./excelService";
import {
  executeListSheetsTool,
  executeReadRangeTool,
} from "./executorReadTools";
import {
  EXECUTOR_DIAGNOSTIC_TOOLS,
  executeFindCalcErrorsTool,
  executeFindCircularReferencesTool,
} from "./executorDiagnosticTools";
import {
  EXECUTOR_WRITE_TOOLS,
  executeActivateSheetTool,
  executeClearRangeTool,
  executeCopyWorksheetTool,
  executeCreateSheetTool,
  executeEditRangeStructureTool,
  executeFormatRangeTool,
  executeInsertCompanyLogoTool,
  executeLayoutRangeTool,
  executeManageNamedRangeTool,
  executeManageWorksheetTool,
  executeModifyWorkbookTool,
  executeRenameSheetTool,
  executeSetRangeFormulasTool,
  executeSetRangeValuesTool,
  executeSortRangeTool,
} from "./executorWriteTools";
import {
  EXECUTOR_REFRESH_TOOLS,
  executeRefreshFactSetDataTool,
  executeRecalculateWorkbookTool,
  type ExecutorRefreshFactSetData,
  type ExecutorRecalculateWorkbook,
} from "./executorRefreshTools";
import {
  EXECUTOR_ANNOTATION_TOOLS,
  executeDeleteCommentTool,
  executeDeleteNoteTool,
  executeGetAllCommentsTool,
  executeGetAllNotesTool,
  executeGetCommentTool,
  executeGetNoteTool,
  executeSetCommentTool,
  executeSetNoteTool,
} from "./executorAnnotationTools";
import {
  ACTIVE_GRAPH_TOOLS,
  executeActiveGraphTool,
} from "./chartCreatorTools";
import {
  isExplicitForwardPeRequest,
  resolveForwardPeIntentSignals,
} from "./chartCapabilityRegistry";
import {
  COMMIT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME,
  ID_LOOKUP_WORKLOAD_TOOLS,
  IdLookupWorkloadController,
  NEXT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME,
  START_ID_LOOKUP_WORKLOAD_TOOL_NAME,
} from "./idLookupWorkload";
import {
  COMPARE_COMPANY_NAME_COLUMNS_TOOL,
  COMPARE_COMPANY_NAME_COLUMNS_TOOL_NAME,
  executeCompareCompanyNameColumnsTool,
} from "./companyNameMatchTool";
import {
  COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME,
  NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME,
  START_TABULAR_QUERY_WORKLOAD_TOOL_NAME,
  TABULAR_QUERY_WORKLOAD_TOOLS,
  TabularQueryWorkloadController,
  type ExcelTabularQueryFeatureConfig,
  type TabularIdentityResolver,
  type TabularQueryWorkloadClient,
} from "./tabularQueryWorkload";
import type { ExcelLangfuseRunContext } from "./langfuseTrace";
import type {
  ColumnSizingCellEvidenceMap,
  ColumnSizingHint,
  ColumnSizingKey,
} from "./columnSizingPolicy";
import type {
  ExcelDateSystem,
  ExcelDateSystemProbeOptions,
} from "./excelDateSystem";
import type { IdLookupRequestedField } from "./idLookupRoutingPolicy";
import type {
  IdLookupOutputScope,
  WorksheetWorkloadOutputScope,
} from "./executorWorkload";
import type {
  ToolCallContext,
  ToolCallDetailedResult,
} from "./toolRegistry";
import {
  EXECUTOR_RANGE_CHART_TOOLS,
  executeRangeChartTool,
} from "./executorNativeChartTools";
import {
  EXECUTOR_NATIVE_CHART_TOOLS,
  executeNativeChartTool,
} from "./executorNativeWorkbookChartTools";
import {
  EXECUTOR_WORKBOOK_TOOLS,
  executeActivateWorkbookTool,
  executeCreateWorkbookTool,
  executeListOpenWorkbooksTool,
  executeOpenWorkbookTool,
} from "./executorWorkbookTools";
import {
  beginChartStoppingEvidence,
  chartStoppingStatusError,
  finishChartStoppingEvidence,
  getChartStoppingEvidence,
  type ChartStoppingEvidence,
} from "./executorChartStopping";
import type {
  ExecutorPlanState,
  ExecutorStepState,
  ExecutorStepStatus,
  ExcelStepStatus,
  PlanStep,
  WorkbookModification,
} from "./types";
import type { FactSetFormulaFormatRegistry } from "./factsetFormulaFormatRegistry";
import type {
  CitationNoteIndex,
  SourceNoteAudit,
} from "./citationNotes";
import type { FactSetSpreadsheetNoteCapability } from "../shared/factset/hostContextService";

// ---------------------------------------------------------------------------
// Runtime context
// ---------------------------------------------------------------------------

/**
 * Callback invoked by control tools when plan state mutates, so the UI
 * can re-render live step progress.
 */
export type PlanStateChangeCallback = (state: ExecutorPlanState) => void;

export type {
  IdLookupOutputScope,
  WorksheetWorkloadOutputScope,
} from "./executorWorkload";

/**
 * Callback supplied by the executor runner to apply a workbook mutation burst.
 * The React host owns sheet reactivation, cancellation, workbook pinning, and
 * operation logging; the tool layer only validates and forwards the normalized
 * `WorkbookModification` payload.
 *
 * @param args - Normalized apply request from `modify_workbook`.
 * @returns The host apply result, including edited cells and deferred refresh metadata.
 */
export type ExecutorApplyWorkbookModifications = (args: {
  /** Mutation payload to apply immediately. */
  modifications: WorkbookModification;
  /** Optional sheet hint for pre-apply reactivation. */
  targetSheet?: string;
  /** Human-readable reason for logs/status, e.g. "executor modify_workbook". */
  reason: string;
  /** Current pinned workbook name for host targeting (may change after re-pin). */
  pinnedWorkbookName?: string;
  /**
   * Set when `modifications` was already run through
   * `normalizeFactSetFormatsForModification` with the run-scoped
   * `factSetFormatRegistry` (see `executeModifyWorkbookTool`). Tells the host
   * apply path (`applyWorkbookModifications` in `excelService.ts`) to skip its
   * own boundary normalization pass, which would otherwise re-run with a fresh
   * empty registry — unable to see this run's accumulated provenance — and
   * redundantly re-scan an already-normalized payload.
   */
  skipFactSetFormatNormalization?: boolean;
}) => Promise<ApplyModificationsResult>;

/**
 * Mutable run-level accumulators updated by write tools. These mirror the
 * fields packed into `ExecutorRunResult` so the multi-hop runner can share the
 * existing `ExcelApp.tsx` finalization path.
 */
export interface ExecutorRunAccumulators {
  /** Internal execution log lines used by verifier/final summary logic. */
  stepResults: string[];
  /** Internal map from executor step id to index in `stepResults`. */
  stepResultIndexById: Map<string, number>;
  /** User-facing step summaries, index-aligned with the current plan steps. */
  stepDisplayResults: string[];
  /** Persistable step statuses, index-aligned with the current plan steps. */
  stepStatuses: ExcelStepStatus[];
  /** Last successfully applied workbook modification payload. */
  lastModifications: WorkbookModification | null;
  /** All edited cell/range addresses returned by successful apply calls. */
  editedCells: string[];
  /** Concrete formula-write ranges that need a final hard-error read-back. */
  formulaWriteTargets: Array<{ sheetName: string; address: string }>;
  /** FDS formula ranges written this run that must not be replaced by guessed static data. */
  fdsFormulaWriteTargets?: Array<{ sheetName: string; address: string }>;
  /** Confirmed FDS formula-write count keyed by normalized `sheet!cell`. */
  fdsFormulaWriteAttempts?: Record<string, number>;
  /** Exact FDS cells verified unavailable after correction + refresh/recalc. */
  unavailableFdsFormulaTargets?: Array<{ sheetName: string; address: string }>;
  /** FactSet date-axis anchors and whether they return serials or YYYYMMDD keys. */
  fdsDateAxisWriteTargets?: Array<{
    sheetName: string;
    address: string;
    kind: "serial" | "key";
  }>;
  /** Worksheets touched by successful writes and eligible for one final autofit pass. */
  autofitSheetNames: Set<string>;
  /** Concrete content-write ranges whose columns are eligible for final autofit. */
  autofitTargets: Array<{ sheetName: string; address: string }>;
  /** Per-column text evidence used to size narrative columns in the final pass. */
  columnSizingHints?: ColumnSizingHint[];
  /** Address-keyed text evidence so rewrites replace prior sizing observations. */
  columnSizingCellEvidence?: ColumnSizingCellEvidenceMap;
  /** Columns sized explicitly this run, which automatic sizing must not override. */
  explicitColumnWidthKeys?: ColumnSizingKey[];
  /** Sheets with FDS-bound formulas that need trailing FactSet refresh. */
  fdsRefreshSheets: Set<string>;
  /** Whether any apply result requested a trailing refresh-all. */
  deferredRefreshAllRequested: boolean;
  /** Deferred recalc mode requested by workbook operations, if any. */
  deferredRecalcMode: "recalculate" | "full" | "fullRebuild" | null;
  /** Whether any workbook edit was attempted during this run. */
  workbookEditAttempted: boolean;
  /** Exact research citation metadata available to writes in this run. */
  citationNoteIndex?: CitationNoteIndex;
  /** Verified Sheet!A1 to exact citation-ID provenance for this run. */
  citationCellProvenance?: Record<string, string[]>;
  /** Citation Note read-back audits produced by cited writes. */
  sourceNoteAudits?: SourceNoteAudit[];
  /** Existing chat-persistence map populated from cited `<u>` values. */
  retrievedCitedValuesByUuid?: Record<string, string>;
}

/**
 * Lightweight per-run session state that write tools need but should not
 * derive themselves from global UI state.
 */
export interface ExecutorRunSessionState {
  /** Sheet captured at run start for safe apply fallback. */
  sessionLockSheet: string;
  /** Exact user request for honoring explicit formatting overrides. */
  userRequest?: string;
  /** Optional workbook pin for host writes. Mutated by apply_template re-pin. */
  pinnedWorkbookName?: string;
  /**
   * Set by apply_template resolution when the remaining steps cannot safely
   * execute (warning-stop). The runner returns this so the shared finalize
   * tail can skip the post-plan verification pass.
   */
  skipFinalVerificationReason?: string;
  /** Allows an explicit user-requested formula-to-values conversion. */
  allowFdsFormulaOverwriteWithStaticValues?: boolean;
  /** True when this run restored an existing checkpoint. */
  isContinuation?: boolean;
  /** Host capability snapshot for generated citation Notes. */
  citationNoteCapability?: FactSetSpreadsheetNoteCapability;
  /** Run-scoped circuit breaker after a citation Note transport failure. */
  citationNoteAttachmentDisabled?: boolean;
  /** Output-specific chart verification keyed by stable run step id. */
  chartStoppingEvidence?: Map<string, ChartStoppingEvidence>;
  /**
   * Set once create_native_chart has inserted and refreshed the target
   * workbook. Suppresses automatic same-run read-back even when the host timed
   * out returning optional chart metadata.
   */
  nativeChartCreateCompletedWorkbookName?: string;
  /**
   * Set after create_native_chart completes its own GetCharts verification.
   * Suppresses a redundant read_native_chart host pass in the same run only.
   */
  nativeChartCreateVerifiedWorkbookName?: string;
}

export interface ExecutorWorkbookHandoff {
  sourceWorkbookName?: string;
  targetWorkbookName: string;
  activeSheetName?: string;
  reason:
    | "apply_template"
    | "native_chart"
    | "open_workbook"
    | "create_workbook"
    | "save_workbook_as"
    | "activate_workbook";
}

/**
 * Per-run context that control tool executors close over. Created once
 * at agent run start and shared across all tool calls within that run.
 */
export interface ExecutorToolRuntimeContext {
  /** The mutable plan state for this run — tools mutate this directly. */
  planState: ExecutorPlanState;
  /** Called after every mutation so the UI reflects live progress. */
  onPlanStateChange: PlanStateChangeCallback;
  /** Applies a normalized workbook modification burst through the host. */
  applyWorkbookModifications: ExecutorApplyWorkbookModifications;
  /** Run-level result accumulators shared with write tools. */
  accumulators: ExecutorRunAccumulators;
  /** Workbook/sheet session state for this executor run. */
  session: ExecutorRunSessionState;
  /**
   * Moves the in-flight conversation/plan to a newly activated workbook.
   * The callback must complete before target-workbook plan updates continue.
   */
  onWorkbookHandoff?: (
    handoff: ExecutorWorkbookHandoff,
  ) => void | Promise<void>;
  /**
   * Announces that a tool is about to create a workbook and move this
   * conversation into it. Excel brings a new workbook forward the moment it is
   * created — seconds before its sheets exist and the run can call
   * `onWorkbookHandoff` — and the task pane otherwise follows Excel and shows
   * that workbook's empty chat in between. While the returned release callback
   * is outstanding the pane stays on this conversation. Always release it.
   */
  beginWorkbookHandoff?: () => () => void;
  /** Identity fields authorized by the detected user request. */
  idLookupRequestedFields?: readonly IdLookupRequestedField[];
  /** Whether this exact executor run exposes worksheet workload tools. */
  idLookupWorkloadEnabled?: boolean;
  /** Server-gated tabular capability and run-snapshotted concurrency limits. */
  excelTabularQuery?: ExcelTabularQueryFeatureConfig;
  /** Trusted source/file scope captured when the user submitted this run. */
  researchContext?: ToolCallContext["researchContext"];
  /**
   * Exact resolved guidance for user-authored Skills, applied to every tabular
   * interpretation this run performs. Deliberately not part of the workload's
   * checkpointed query options: a restored workload re-derives it from the live
   * run context, so a resumed run can never replay a stale policy.
   */
  userSkillGuidance?: ToolCallContext["userSkillGuidance"];
  /** Injectable one-to-one client used by focused workload tests. */
  tabularQueryClient?: TabularQueryWorkloadClient;
  /** Run attribution propagated to tabular query HTTP requests. */
  langfuseContext?: ExcelLangfuseRunContext;
  /** Injectable PR #535 identity resolver used by focused workload tests. */
  tabularIdentityResolver?: TabularIdentityResolver;
  /** Injectable Office.js date-system probe; the workload caches it per workbook pin. */
  probeExcelDateSystem?: (
    options?: ExcelDateSystemProbeOptions,
  ) => Promise<ExcelDateSystem | null>;
  /** Registered worksheet target used by write guards to reject adjacent writes. */
  worksheetWorkloadOutputScope?: WorksheetWorkloadOutputScope;
  /** @deprecated Backward-compatible ID-lookup reservation alias. */
  idLookupOutputScope?: IdLookupOutputScope;
  /** Run cancellation signal used to release run-scoped workload reservations. */
  signal?: AbortSignal;
  /** Current iteration budget, including a start call executing now. */
  getRemainingExecutorIterations?: () => number;
  /** Formula provenance and value semantics captured during this run. */
  factSetFormatRegistry?: FactSetFormulaFormatRegistry;
  /** Optional: refresh FactSet data for sheets mid-run. */
  refreshFactSetData?: ExecutorRefreshFactSetData;
  /** Optional: trigger workbook recalculation mid-run. */
  recalculateWorkbook?: ExecutorRecalculateWorkbook;
  /**
   * Steps a checkpoint already completed.
   *
   * A resumed run builds on this work; it does not get to revisit it. The
   * prompt asks the model to continue only what is queued, but a prompt is a
   * request — this is the mechanism. Reopening or dropping a completed step
   * would rewrite what the user was already told is done, and for a write step
   * would invite a replay of edits the workbook already has.
   */
  lockedStepIds?: readonly string[];
}

// ---------------------------------------------------------------------------
// set_step_status
// ---------------------------------------------------------------------------

export const SET_STEP_STATUS_TOOL: ChartCapabilityToolDef = {
  name: "set_step_status",
  description:
    "Update the status of a run step. Call this to mark a step as running, " +
    "verifying, success, warning, failed, recovered, or paused. Optionally " +
    "attach a note explaining the final outcome. Choose the status from the " +
    "final verified state: warning only when a client-impacting issue still " +
    "exists; recovered when an earlier issue was corrected and verified; " +
    "success when no remediation was needed. The selected status is preserved exactly.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      stepId: {
        type: "string",
        description:
          "Stable ID of an existing step from the current run steps. Use the exact id " +
          "shown by set_run_plan/revise_plan; do not create or guess ids for this tool.",
      },
      status: {
        type: "string",
        enum: [
          "queued",
          "running",
          "verifying",
          "success",
          "warning",
          "failed",
          "recovered",
          "paused",
        ],
        description:
          "Status chosen from the final observed state. Use success when the intent " +
          "is verified with no remediation, recovered when a prior issue was corrected " +
          "and the final result is verified, warning only for a concrete client-impacting " +
          "caveat that remains, failed when the step could not be completed, and paused " +
          "only for a retryable host condition.",
      },
      note: {
        type: ["string", "null"],
        description:
          "Optional final-outcome note. For warning, state the concrete issue that still " +
          "remains. For success or recovered, describe only the verified result and omit " +
          "temporary implementation issues. Pass null when no note is needed.",
      },
    },
    required: ["stepId", "status", "note"],
    additionalProperties: false,
  },
};

/** Validated input for `set_step_status`. */
export interface SetStepStatusInput {
  /**
   * Stable ID of an existing step from the current run steps.
   * Use the exact id shown by set_run_plan/revise_plan; do not create or guess ids.
   */
  stepId: string;
  /** New status for the step. */
  status: ExecutorStepStatus;
  /** Optional note explaining the transition. Null clears any existing note. */
  note: string | null;
}

/** Result returned by `set_step_status`. */
export interface SetStepStatusResult {
  /** Whether the update was applied successfully. */
  ok: boolean;
  /** Error message if the update failed (e.g. unknown stepId). */
  error?: string;
  /** Model-facing recovery hint for correctable sequencing mistakes. */
  instruction?: string;
  /** Present when the runtime applied a status other than the requested one. */
  status?: ExecutorStepStatus;
  /** The note actually recorded, when the runtime amended it. */
  note?: string;
}

// ---------------------------------------------------------------------------
// revise_plan
// ---------------------------------------------------------------------------

export const REVISE_PLAN_TOOL: ChartCapabilityToolDef = {
  name: "revise_plan",
  description:
    "Revise the run step checklist mid-run. Use when the agent discovers the " +
    "current steps are incorrect or incomplete — e.g. a step needs to be " +
    "inserted, removed, reordered, or the summary updated. All steps in the " +
    "revised checklist must have a unique id. Reuse the exact existing id for " +
    "the same logical step, even if order, wording, status, note, or target " +
    "sheet changed. Only create a new id for a genuinely new step, using " +
    "format `step_<number>_<short_slug>` (for example, `step_3_verify_outputs`).",
  inputSchema: {
    type: "object",
    properties: {
      summary: {
        type: "string",
        description: "Updated run summary.",
      },
      steps: {
        type: "array",
        items: {
          type: "object",
          properties: {
            id: {
              type: "string",
              description:
                "Stable step identifier. Reuse the exact existing id for the same logical " +
                "step. Only create a new id for a genuinely new step, using format " +
                "`step_<number>_<short_slug>` (e.g. `step_3_verify_outputs`). Must be unique.",
            },
            step: {
              type: "integer",
              description: "Step number (1-based).",
            },
            description: {
              type: "string",
              description: "What this step does.",
            },
            action: {
              type: "string",
              description: "Brief action type: fill, format, formula, table, chart, etc.",
            },
            targetSheet: {
              type: ["string", "null"],
              description: "Which sheet this step operates on. Null for active sheet.",
            },
            applyTemplate: {
              anyOf: [
                {
                  type: "object",
                  properties: {
                    templateKey: {
                      type: "string",
                      description: "Template key selected by retrieve_template.",
                    },
                    companyIdentifier: {
                      type: ["string", "null"],
                      description: "Optional SYMBOL-COUNTRY FactSet identifier (e.g. AAPL-US).",
                    },
                    targetWorkbook: {
                      type: ["string", "null"],
                      enum: ["current", "new", null],
                      description: "Whether to apply the template to the current or a new workbook.",
                    },
                  },
                  required: ["templateKey", "companyIdentifier", "targetWorkbook"],
                  additionalProperties: false,
                },
                { type: "null" },
              ],
              description:
                "Present only for apply_template steps. Pass null for all other action types.",
            },
            status: {
              type: "string",
              enum: [
                "queued",
                "running",
                "verifying",
                "success",
                "warning",
                "failed",
                "recovered",
                "paused",
              ],
              description:
                "Status chosen from the final observed state. Warning means a concrete " +
                "client-impacting caveat remains; recovered means an earlier issue was " +
                "corrected and the final result was verified.",
            },
            note: {
              type: ["string", "null"],
              description:
                "Final-outcome note only. A warning note names the remaining issue; a " +
                "success or recovered note omits corrected implementation detours.",
            },
          },
          required: [
            "id",
            "step",
            "description",
            "action",
            "targetSheet",
            "applyTemplate",
            "status",
            "note",
          ],
          additionalProperties: false,
        },
        description: "Complete revised step list (replaces the current run steps).",
      },
    },
    required: ["summary", "steps"],
    additionalProperties: false,
  },
};

/** One step in the `revise_plan` input. */
export interface RevisePlanStepInput {
  /**
   * Stable step identifier. Reuse the exact existing id for the same
   * logical step; create a new `step_<number>_<short_slug>` id only for
   * genuinely new steps.
   */
  id: string;
  /** Step number (1-based). */
  step: number;
  /** What this step does. */
  description: string;
  /** Brief action type. */
  action: string;
  /** Target sheet or null for active. */
  targetSheet: string | null;
  /** Template metadata for apply_template steps; null for all other steps. */
  applyTemplate: {
    /** Template key selected by retrieve_template. */
    templateKey: string;
    /** Optional SYMBOL-COUNTRY FactSet identifier (e.g. AAPL-US). */
    companyIdentifier: string | null;
    /** Whether to apply to the current or a new workbook. */
    targetWorkbook: "current" | "new" | null;
  } | null;
  /** Current status for this step. */
  status: ExecutorStepStatus;
  /** Optional note. */
  note: string | null;
}

/** Validated input for `revise_plan`. */
export interface RevisePlanInput {
  /** Updated run summary. */
  summary: string;
  /** Complete revised step list. */
  steps: RevisePlanStepInput[];
}

/** Result returned by `revise_plan`. */
export interface RevisePlanResult {
  /** Whether the revision was applied successfully. */
  ok: boolean;
  /** Number of steps in the revised run checklist. */
  stepCount?: number;
  /** Error message if the revision failed. */
  error?: string;
  /** How to produce a revision that will be accepted. */
  instruction?: string;
}

// ---------------------------------------------------------------------------
// set_run_plan
// ---------------------------------------------------------------------------

export const SET_RUN_PLAN_TOOL: ChartCapabilityToolDef = {
  name: "set_run_plan",
  description:
    "Set the run step checklist before making workbook edits. Use this " +
    "when the user request requires workbook changes. Do not call it for pure " +
    "answers or clarification questions.",
  inputSchema: {
    type: "object",
    properties: {
      summary: { type: "string", description: "Concise run summary." },
      estimatedComplexity: {
        type: "string",
        enum: ["simple", "moderate", "complex"],
        description: "Estimated complexity of the workbook work.",
      },
      steps: {
        type: "array",
        items: {
          type: "object",
          properties: {
            id: {
              type: "string",
              description: "Stable id such as step_1_create_table. Must be unique.",
            },
            step: { type: "integer", description: "1-based step number." },
            description: { type: "string", description: "What this step does." },
            action: { type: "string", description: "Brief action type." },
            targetSheet: {
              type: ["string", "null"],
              description: "Target worksheet or null for active sheet.",
            },
          },
          required: ["id", "step", "description", "action", "targetSheet"],
          additionalProperties: false,
        },
      },
    },
    required: ["summary", "estimatedComplexity", "steps"],
    additionalProperties: false,
  },
};

interface SetRunPlanInput {
  summary: string;
  estimatedComplexity: "simple" | "moderate" | "complex";
  steps: Array<{
    id: string;
    step: number;
    description: string;
    action: string;
    targetSheet: string | null;
  }>;
}

interface SetRunPlanResult {
  ok: boolean;
  stepCount?: number;
  error?: string;
  instruction?: string;
}

// ---------------------------------------------------------------------------
// Tool defs aggregate
// ---------------------------------------------------------------------------

/** All control tool definitions for the multi-hop executor. */
export const EXECUTOR_CONTROL_TOOLS: readonly ChartCapabilityToolDef[] = [
  SET_RUN_PLAN_TOOL,
  SET_STEP_STATUS_TOOL,
  REVISE_PLAN_TOOL,
];

/** All run-scoped tool definitions for the multi-hop executor. */
export const EXECUTOR_RUNTIME_TOOLS: readonly ChartCapabilityToolDef[] = [
  ...EXECUTOR_CONTROL_TOOLS,
  ...EXECUTOR_ANNOTATION_TOOLS,
  ...CHART_CAPABILITY_TOOLS,
  ...ACTIVE_GRAPH_TOOLS,
  ...EXECUTOR_WRITE_TOOLS,
  ...EXECUTOR_RANGE_CHART_TOOLS,
  ...EXECUTOR_NATIVE_CHART_TOOLS,
  ...EXECUTOR_REFRESH_TOOLS,
  ...EXECUTOR_WORKBOOK_TOOLS,
  ...EXECUTOR_DIAGNOSTIC_TOOLS,
];

// ---------------------------------------------------------------------------
// Executor factory
// ---------------------------------------------------------------------------

/**
 * Executor function signature matching the global `ToolExecutor` type.
 * Detailed results retain trace-only child metadata without exposing it to the
 * model-facing output.
 */
export type RuntimeToolExecutor = (
  rawInput: unknown,
  signal?: AbortSignal,
  callContext?: RuntimeToolCallContext,
) => Promise<string | ToolCallDetailedResult>;

export interface RuntimeWorkloadProgress {
  kind: "requests" | "closed_rows";
  completed: number;
  total: number;
  usable?: number;
  failed?: number;
}

/** Display-only context for the currently executing runtime tool call. */
export interface RuntimeToolCallContext {
  callId: string;
  iteration: number;
  reportWorkloadProgress?: (progress: RuntimeWorkloadProgress) => void;
}

async function executeFocusedWrite(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
  executor: (ctx: ExecutorToolRuntimeContext, rawInput: unknown) => Promise<string>,
): Promise<string> {
  const modelInput =
    rawInput !== null && typeof rawInput === "object" && !Array.isArray(rawInput)
      ? rawInput as Record<string, unknown>
      : {};
  const result = await executor(ctx, {
    ...modelInput,
    stepId: ctx.planState.currentStepId ?? null,
  });
  try {
    const parsed = JSON.parse(result) as Record<string, unknown>;
    delete parsed.stepId;
    return JSON.stringify(parsed);
  } catch {
    return result;
  }
}

async function executeActiveGraphWithStopping(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
  signal?: AbortSignal,
): Promise<string> {
  const record =
    rawInput && typeof rawInput === "object" && !Array.isArray(rawInput)
      ? (rawInput as Record<string, unknown>)
      : {};
  const operation =
    typeof record.operation === "string" ? record.operation : "";
  if (
    operation !== "create_active_graph" &&
    operation !== "edit_active_graph"
  ) {
    return executeActiveGraphTool(
      injectActiveGraphTargetSheet(rawInput, ctx.session),
      signal,
      activeGraphSemanticUserRequest(ctx, rawInput),
    );
  }
  const stepId = beginChartStoppingEvidence(ctx, {
    stepId: record.stepId,
    tool: "active_graph",
    operation,
  });
  if (!stepId) {
    return JSON.stringify({
      ok: false,
      applied: false,
      operation,
      error:
        `${operation} requires a valid stepId (or an unambiguous active chart step) before it can mutate a workbook.`,
      completionEvidence: { satisfied: false },
    });
  }
  const result = await executeActiveGraphTool(
    injectActiveGraphTargetSheet(rawInput, ctx.session),
    signal,
    activeGraphSemanticUserRequest(ctx, rawInput),
  );
  let parsed: Record<string, unknown> = {};
  try {
    parsed = JSON.parse(result) as Record<string, unknown>;
  } catch {
    // The unsatisfied evidence below reports the unreadable acknowledgement.
  }
  const completion =
    parsed.completionEvidence &&
    typeof parsed.completionEvidence === "object" &&
    !Array.isArray(parsed.completionEvidence)
      ? (parsed.completionEvidence as Record<string, unknown>)
      : {};
  const satisfied =
    parsed.ok === true &&
    parsed.applied === true &&
    completion.satisfied === true;
  finishChartStoppingEvidence(ctx, {
    stepId,
    tool: "active_graph",
    operation,
    satisfied,
    reason: satisfied
      ? undefined
      : typeof parsed.error === "string"
        ? parsed.error
        : "ActiveGraph preview, host mutation, and OLE read-back were not all confirmed.",
    details: completion,
  });
  return result;
}

/**
 * Creates run-scoped tool executors that close over the given runtime
 * context. These executors mutate `ctx.planState` directly and invoke
 * `ctx.onPlanStateChange` after every mutation.
 *
 * The returned map is keyed by tool name and used by the agent's
 * dispatch wrapper: it tries these first, then falls back to the
 * global `executeToolCallDetailed` for stateless tools.
 *
 * @param ctx - The per-run runtime context holding mutable plan state.
 * @returns Object with:
 *   - `toolDefs`: tool definitions to include in the model's tool list.
 *   - `executors`: name→executor map for dispatch.
 */
export function createExecutorToolRuntime(ctx: ExecutorToolRuntimeContext) {
  const idLookupWorkload = new IdLookupWorkloadController(ctx);
  const tabularQueryWorkload = new TabularQueryWorkloadController(ctx);
  const idLookupWorkloadEnabled = ctx.idLookupWorkloadEnabled === true;
  const tabularQueryWorkloadEnabled =
    ctx.excelTabularQuery?.enabled === true;
  const workloadTools = [
    ...(idLookupWorkloadEnabled
      ? [...ID_LOOKUP_WORKLOAD_TOOLS, COMPARE_COMPANY_NAME_COLUMNS_TOOL]
      : []),
    ...(tabularQueryWorkloadEnabled ? TABULAR_QUERY_WORKLOAD_TOOLS : []),
  ];
  return {
    toolDefs:
      workloadTools.length > 0
        ? [...EXECUTOR_RUNTIME_TOOLS, ...workloadTools]
        : EXECUTOR_RUNTIME_TOOLS,
    executors: {
      set_run_plan: (rawInput) => executeSetRunPlan(ctx, rawInput),
      set_step_status: (rawInput) => executeSetStepStatus(ctx, rawInput),
      revise_plan: (rawInput) => executeRevisePlan(ctx, rawInput),
      modify_workbook: (rawInput) => executeModifyWorkbookTool(ctx, rawInput),
      set_range_values: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeSetRangeValuesTool),
      set_range_formulas: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeSetRangeFormulasTool),
      format_range: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeFormatRangeTool),
      clear_range: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeClearRangeTool),
      edit_range_structure: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeEditRangeStructureTool),
      layout_range: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeLayoutRangeTool),
      sort_range: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeSortRangeTool),
      manage_worksheet: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeManageWorksheetTool),
      copy_worksheet: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeCopyWorksheetTool),
      manage_named_range: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeManageNamedRangeTool),
      insert_company_logo: (rawInput) =>
        executeFocusedWrite(ctx, rawInput, executeInsertCompanyLogoTool),
      create_sheet: (rawInput) => executeCreateSheetTool(ctx, rawInput),
      rename_sheet: (rawInput) => executeRenameSheetTool(ctx, rawInput),
      activate_sheet: (rawInput) => executeActivateSheetTool(ctx, rawInput),
      list_sheets: (rawInput) =>
        executeListSheetsTool(injectWorkbookName(rawInput, ctx.session)),
      read_range: (rawInput) =>
        executeReadRangeTool(injectWorkbookName(rawInput, ctx.session)),
      find_circular_references: (rawInput, signal) =>
        executeFindCircularReferencesTool(ctx, rawInput, signal),
      find_calc_errors: (rawInput, signal) =>
        executeFindCalcErrorsTool(ctx, rawInput, signal),
      get_comment: (rawInput, signal) =>
        executeGetCommentTool(rawInput, ctx.session.pinnedWorkbookName, signal),
      set_comment: (rawInput) => executeSetCommentTool(ctx, rawInput),
      delete_comment: (rawInput) => executeDeleteCommentTool(ctx, rawInput),
      get_all_comments: (rawInput, signal) =>
        executeGetAllCommentsTool(
          rawInput,
          ctx.session.pinnedWorkbookName,
          signal,
        ),
      get_note: (rawInput, signal) =>
        executeGetNoteTool(rawInput, ctx.session.pinnedWorkbookName, signal),
      set_note: (rawInput) => executeSetNoteTool(ctx, rawInput),
      delete_note: (rawInput) => executeDeleteNoteTool(ctx, rawInput),
      get_all_notes: (rawInput, signal) =>
        executeGetAllNotesTool(
          rawInput,
          ctx.session.pinnedWorkbookName,
          signal,
        ),
      list_chart_types: async (rawInput) =>
        executeChartCapabilityTool("list_chart_types", rawInput),
      active_graph: (rawInput, signal?: AbortSignal) =>
        executeActiveGraphWithStopping(ctx, rawInput, signal),
      range_chart: (rawInput) =>
        executeRangeChartTool(ctx, injectWorkbookName(rawInput, ctx.session)),
      native_chart: (rawInput, signal?: AbortSignal) =>
        executeNativeChartTool(ctx, rawInput, signal),
      // Dispatcher-only compatibility alias for persisted/paused runs. It is
      // intentionally absent from EXECUTOR_RUNTIME_TOOLS, so the model never
      // sees the old tool name.
      native_graph: (rawInput) =>
        executeRangeChartTool(ctx, injectWorkbookName(rawInput, ctx.session)),
      refresh_factset_data: (rawInput) =>
        executeRefreshFactSetDataTool(ctx, rawInput),
      recalculate_workbook: (rawInput) =>
        executeRecalculateWorkbookTool(ctx, rawInput),
      list_open_workbooks: (rawInput) =>
        executeListOpenWorkbooksTool(ctx, rawInput),
      open_workbook: (rawInput, signal) =>
        executeOpenWorkbookTool(ctx, rawInput, signal),
      create_workbook: (rawInput, signal) =>
        executeCreateWorkbookTool(ctx, rawInput, signal),
      activate_workbook: (rawInput, signal) =>
        executeActivateWorkbookTool(ctx, rawInput, signal),
      ...(idLookupWorkloadEnabled
        ? {
            [START_ID_LOOKUP_WORKLOAD_TOOL_NAME]: (rawInput: unknown) =>
              idLookupWorkload.start(rawInput),
            [NEXT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME]: (
              rawInput: unknown,
              signal?: AbortSignal,
            ) => idLookupWorkload.lookupNextWave(rawInput, signal),
            [COMMIT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME]: (rawInput: unknown) =>
              idLookupWorkload.commitWave(rawInput),
            [COMPARE_COMPANY_NAME_COLUMNS_TOOL_NAME]: (
              rawInput: unknown,
              signal?: AbortSignal,
            ) =>
              executeCompareCompanyNameColumnsTool(ctx, rawInput, signal),
          }
        : {}),
      ...(tabularQueryWorkloadEnabled
        ? {
            [START_TABULAR_QUERY_WORKLOAD_TOOL_NAME]: (
              rawInput: unknown,
              signal?: AbortSignal,
              callContext?: RuntimeToolCallContext,
            ) => tabularQueryWorkload.start(rawInput, signal, callContext),
            [NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME]: (
              rawInput: unknown,
              signal?: AbortSignal,
              callContext?: RuntimeToolCallContext,
            ) =>
              tabularQueryWorkload.executeNextWave(
                rawInput,
                signal,
                callContext,
              ),
            [COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME]: (
              rawInput: unknown,
              signal?: AbortSignal,
              callContext?: RuntimeToolCallContext,
            ) => tabularQueryWorkload.commitWave(rawInput, signal, callContext),
          }
        : {}),
    } satisfies Record<string, RuntimeToolExecutor>,
    idLookupWorkload,
    tabularQueryWorkload,
  };
}

/**
 * Initializes or replaces the live executor run steps from the model's first
 * `set_run_plan` call and resets result accumulators to match the new steps.
 */
async function executeSetRunPlan(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  if (ctx.session.isContinuation || ctx.planState.steps.length > 0) {
    return JSON.stringify({
      ok: false,
      error: "Run steps already exist and cannot be replaced during execution.",
      instruction:
        "Continue with the existing stable step IDs. Use revise_plan only for a necessary non-destructive adjustment.",
    } satisfies SetRunPlanResult);
  }
  const input = normaliseSetRunPlanInput(rawInput);
  if (!input.summary.trim()) {
    return JSON.stringify({ ok: false, error: "Missing required parameter: summary" } satisfies SetRunPlanResult);
  }
  if (input.steps.length === 0) {
    return JSON.stringify({ ok: false, error: "Run steps must contain at least one step." } satisfies SetRunPlanResult);
  }
  const ids = input.steps.map((step) => step.id);
  const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index);
  if (ids.some((id) => !id.trim())) {
    return JSON.stringify({ ok: false, error: "All steps must have a non-empty id." } satisfies SetRunPlanResult);
  }
  if (duplicateIds.length > 0) {
    return JSON.stringify({
      ok: false,
      error: `Duplicate step ids: ${Array.from(new Set(duplicateIds)).join(", ")}`,
    } satisfies SetRunPlanResult);
  }

  ctx.planState.summary = input.summary;
  ctx.planState.estimatedComplexity = input.estimatedComplexity;
  ctx.planState.steps = input.steps.map((step) => ({
    id: step.id,
    planStep: {
      step: step.step,
      description: step.description,
      action: step.action,
      targetSheet: step.targetSheet ?? undefined,
    },
    status: "queued",
  } satisfies ExecutorStepState));
  ctx.planState.currentStepId = undefined;
  ctx.accumulators.stepResults = [];
  ctx.accumulators.stepResultIndexById = new Map();
  ctx.accumulators.stepDisplayResults = input.steps.map((step) => step.description);
  ctx.accumulators.stepStatuses = input.steps.map(() => "queued");
  ctx.onPlanStateChange(ctx.planState);

  return JSON.stringify({
    ok: true,
    stepCount: ctx.planState.steps.length,
  } satisfies SetRunPlanResult);
}

// ---------------------------------------------------------------------------
// set_step_status executor
// ---------------------------------------------------------------------------

/**
 * Applies a status transition to a single step in the plan state.
 * Updates `currentStepId` when a step transitions to `running` or `verifying`.
 * Clears `currentStepId` when the current step reaches a terminal state.
 *
 * @param ctx - Runtime context with mutable plan state.
 * @param rawInput - Parsed LLM arguments for `set_step_status`.
 * @returns JSON string conforming to {@link SetStepStatusResult}.
 */
async function executeSetStepStatus(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  const input = normaliseSetStepStatusInput(rawInput);

  if (ctx.planState.steps.length === 0) {
    return JSON.stringify({
      ok: false,
      error: "Create run steps before updating step status.",
      instruction:
        "Call set_run_plan next to create run steps, then continue the workbook-edit workflow with those step ids.",
    } satisfies SetStepStatusResult);
  }

  if (!input.stepId.trim()) {
    return JSON.stringify({
      ok: false,
      error: "Missing required parameter: stepId",
    } satisfies SetStepStatusResult);
  }

  if (!isValidExecutorStepStatus(input.status)) {
    return JSON.stringify({
      ok: false,
      error: `Invalid status: "${input.status}". Must be one of: queued, running, verifying, success, warning, failed, recovered, paused.`,
    } satisfies SetStepStatusResult);
  }

  const step = ctx.planState.steps.find((s) => s.id === input.stepId);
  if (!step) {
    const available = ctx.planState.steps.map((s) => s.id).join(", ");
    return JSON.stringify({
      ok: false,
      error: `Unknown stepId: "${input.stepId}". Available: ${available}`,
    } satisfies SetStepStatusResult);
  }

  const chartStoppingError = chartStoppingStatusError(ctx, step, input.status);
  if (chartStoppingError) {
    return JSON.stringify({
      ok: false,
      error: chartStoppingError,
      instruction:
        "Keep the step running or verifying, run the chart operation/read-back required for this output mode, then retry the terminal status.",
    } satisfies SetStepStatusResult);
  }

  // A chart with an advisory warning (values still resolving, say) is not a
  // clean success. Downgrade rather than reject, and carry the caveat onto the
  // step either way: the model choosing "warning" itself is no reason for the
  // user to lose what the chart tool actually observed.
  const chartWarning =
    input.status === "success" || input.status === "warning"
      ? getChartStoppingEvidence(ctx, step.id)?.warning
      : undefined;
  const status: ExecutorStepStatus =
    chartWarning && input.status === "success" ? "warning" : input.status;

  // Restating a locked step's own status is a harmless no-op; changing it is
  // the resumed run reopening work the user was told had finished.
  if (isLockedStep(ctx, step.id) && status !== step.status) {
    return JSON.stringify({
      ok: false,
      error: `Step "${step.id}" completed in an earlier run and cannot be reopened.`,
      instruction:
        "Continue with the steps that are still queued. If this work genuinely has to be redone, " +
        "add a new step for it with revise_plan rather than changing a completed one.",
    } satisfies SetStepStatusResult);
  }

  const requestedNote = input.note?.trim() || null;
  const cleanNote =
    chartWarning && !requestedNote?.includes(chartWarning)
      ? requestedNote
        ? `${requestedNote} ${chartWarning}`
        : chartWarning
      : requestedNote;
  step.status = status;

  if (cleanNote === null) {
    delete step.note;
  } else {
    step.note = cleanNote;
  }

  if (isTerminalStatus(status) || status === "paused") {
    syncStepStatusAccumulators(ctx, step, status, cleanNote);
  }

  if (isActiveStatus(status)) {
    ctx.planState.currentStepId = input.stepId;
  } else if (
    ctx.planState.currentStepId === input.stepId &&
    (isTerminalStatus(status) || status === "paused")
  ) {
    ctx.planState.currentStepId = undefined;
  }

  ctx.onPlanStateChange(ctx.planState);

  return JSON.stringify({
    ok: true,
    // Echo only what the caller did not ask for, so the model can see the
    // downgrade or the appended caveat.
    ...(status !== input.status || cleanNote !== requestedNote
      ? { status, note: cleanNote ?? undefined }
      : {}),
  } satisfies SetStepStatusResult);
}

// ---------------------------------------------------------------------------
// revise_plan executor
// ---------------------------------------------------------------------------

/**
 * Replaces the plan state's summary and steps with a revised version.
 * Validates that every step has a non-empty id and that ids are unique.
 *
 * @param ctx - Runtime context with mutable plan state.
 * @param rawInput - Parsed LLM arguments for `revise_plan`.
 * @returns JSON string conforming to {@link RevisePlanResult}.
 */
async function executeRevisePlan(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  const input = normaliseRevisePlanInput(rawInput);

  if (!input.summary.trim()) {
    return JSON.stringify({
      ok: false,
      error: "Missing required parameter: summary",
    } satisfies RevisePlanResult);
  }

  if (input.steps.length === 0) {
    return JSON.stringify({
      ok: false,
      error: "Revised plan must contain at least one step.",
    } satisfies RevisePlanResult);
  }

  const invalidStep = input.steps.find((s) => !Number.isInteger(s.step) || s.step < 1);
  if (invalidStep) {
    return JSON.stringify({
      ok: false,
      error: `Invalid step number for "${invalidStep.id}": ${invalidStep.step}. Step numbers must be positive integers.`,
    } satisfies RevisePlanResult);
  }

  const missingDescription = input.steps.find((s) => !s.description.trim());
  if (missingDescription) {
    return JSON.stringify({
      ok: false,
      error: `Missing required parameter: description for step "${missingDescription.id}".`,
    } satisfies RevisePlanResult);
  }

  const missingAction = input.steps.find((s) => !s.action.trim());
  if (missingAction) {
    return JSON.stringify({
      ok: false,
      error: `Missing required parameter: action for step "${missingAction.id}".`,
    } satisfies RevisePlanResult);
  }

  const invalidStatus = input.steps.find((s) => !isValidExecutorStepStatus(s.status));
  if (invalidStatus) {
    return JSON.stringify({
      ok: false,
      error: `Invalid status for step "${invalidStatus.id}": "${invalidStatus.status}". Must be one of: queued, running, verifying, success, warning, failed, recovered, paused.`,
    } satisfies RevisePlanResult);
  }

  const invalidApplyTemplate = input.steps.find(
    (s) => s.action === "apply_template" && !s.applyTemplate?.templateKey.trim(),
  );
  if (invalidApplyTemplate) {
    return JSON.stringify({
      ok: false,
      error: `Missing applyTemplate.templateKey for apply_template step "${invalidApplyTemplate.id}".`,
    } satisfies RevisePlanResult);
  }

  const ids = input.steps.map((s) => s.id);
  const uniqueIds = new Set(ids);
  if (uniqueIds.size !== ids.length) {
    const dupes = ids.filter((id, i) => ids.indexOf(id) !== i);
    const uniqueDupes = Array.from(new Set(dupes));
    return JSON.stringify({
      ok: false,
      error: `Duplicate step ids: ${uniqueDupes.join(", ")}`,
    } satisfies RevisePlanResult);
  }

  const emptyIds = ids.filter((id) => !id.trim());
  if (emptyIds.length > 0) {
    return JSON.stringify({
      ok: false,
      error: "All steps must have a non-empty id.",
    } satisfies RevisePlanResult);
  }

  const lockedViolation = lockedStepRevisionError(ctx, input.steps);
  if (lockedViolation) {
    return JSON.stringify({
      ok: false,
      error: lockedViolation,
      instruction:
        "Resend the revision with every completed step kept exactly as it is, and put the new work in additional steps.",
    } satisfies RevisePlanResult);
  }

  const previousAccumulatorState = captureAccumulatorState(ctx);

  ctx.planState.summary = input.summary;
  ctx.planState.steps = input.steps.map((s) => {
    const cleanNote = s.note?.trim() || null;
    return {
      id: s.id,
      planStep: {
        step: s.step,
        description: s.description,
        action: s.action,
        targetSheet: s.targetSheet ?? undefined,
        applyTemplate: s.applyTemplate
          ? {
              templateKey: s.applyTemplate.templateKey,
              companyIdentifier: s.applyTemplate.companyIdentifier ?? undefined,
              targetWorkbook: s.applyTemplate.targetWorkbook ?? undefined,
            }
          : undefined,
      } satisfies PlanStep,
      status: s.status,
      ...(cleanNote ? { note: cleanNote } : {}),
    } satisfies ExecutorStepState;
  });

  const activeStep = ctx.planState.steps.find((s) => isActiveStatus(s.status));
  ctx.planState.currentStepId = activeStep?.id;
  realignAccumulatorsToPlan(ctx, previousAccumulatorState);

  ctx.onPlanStateChange(ctx.planState);

  return JSON.stringify({
    ok: true,
    stepCount: ctx.planState.steps.length,
  } satisfies RevisePlanResult);
}

// ---------------------------------------------------------------------------
// Status mapping
// ---------------------------------------------------------------------------

const VALID_EXECUTOR_STATUSES: ReadonlySet<string> = new Set<ExecutorStepStatus>([
  "queued",
  "running",
  "verifying",
  "success",
  "warning",
  "failed",
  "recovered",
  "paused",
]);

const TERMINAL_STATUSES: ReadonlySet<ExecutorStepStatus> = new Set<ExecutorStepStatus>([
  "success",
  "warning",
  "failed",
  "recovered",
]);

const ACTIVE_STATUSES: ReadonlySet<ExecutorStepStatus> = new Set<ExecutorStepStatus>([
  "running",
  "verifying",
]);

/**
 * Type guard for valid `ExecutorStepStatus` values.
 *
 * @param value - String to validate.
 * @returns True if the value is a member of `ExecutorStepStatus`.
 */
function isValidExecutorStepStatus(value: string): value is ExecutorStepStatus {
  return VALID_EXECUTOR_STATUSES.has(value);
}

/** Whether a checkpoint already completed this step in an earlier run. */
function isLockedStep(ctx: ExecutorToolRuntimeContext, stepId: string): boolean {
  return ctx.lockedStepIds?.includes(stepId) === true;
}

/**
 * Why a revision may not be applied, or nothing.
 *
 * A resumed run may add, reorder, or rewrite the work still ahead of it. The
 * steps a checkpoint already completed are not that work: dropping one erases
 * what the user was told is done, and reopening one invites a replay of writes
 * the workbook already has.
 */
function lockedStepRevisionError(
  ctx: ExecutorToolRuntimeContext,
  revised: readonly RevisePlanStepInput[],
): string | undefined {
  if (!ctx.lockedStepIds?.length) return undefined;
  const revisedById = new Map(revised.map((step) => [step.id, step]));
  for (const current of ctx.planState.steps) {
    if (!isLockedStep(ctx, current.id)) continue;
    const next = revisedById.get(current.id);
    if (!next) {
      return `Step "${current.id}" completed in an earlier run and must stay in the plan.`;
    }
    if (next.status !== current.status) {
      return (
        `Step "${current.id}" completed in an earlier run with status "${current.status}" ` +
        `and cannot be changed to "${next.status}".`
      );
    }
  }
  return undefined;
}

/**
 * Whether a status represents a terminal (completed) state where
 * `currentStepId` should be cleared.
 *
 * @param status - The step status to check.
 * @returns True if the step has reached a terminal state.
 */
function isTerminalStatus(status: ExecutorStepStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}

/**
 * Whether a status represents an actively executing step.
 *
 * @param status - The step status to check.
 * @returns True for statuses that should own `currentStepId`.
 */
function isActiveStatus(status: ExecutorStepStatus): boolean {
  return ACTIVE_STATUSES.has(status);
}

/**
 * Mirrors final step status changes into the result accumulators returned by
 * the runner. `set_step_status` is the executor's final authority after
 * verification, so a later failed/paused/warning status must override any
 * earlier write-tool result for the same step.
 *
 * @param ctx - Runtime context with shared run accumulators.
 * @param step - Step whose persisted result should be updated.
 * @param status - Final or paused status selected by the executor.
 * @param note - Optional status note to include in the user-facing result.
 */
function syncStepStatusAccumulators(
  ctx: ExecutorToolRuntimeContext,
  step: ExecutorStepState,
  status: ExecutorStepStatus,
  note: string | null,
): void {
  const index = ctx.planState.steps.findIndex((s) => s.id === step.id);
  if (index < 0) return;

  ensureAccumulatorSlots(ctx, index);

  const persistedStatus = toPersistedStepStatus(status);
  const cleanNote = note?.trim();
  const description = step.planStep.description;
  const label = step.planStep.step || index + 1;
  const existingResultIndex = ctx.accumulators.stepResultIndexById.get(step.id);
  const alreadyRecorded =
    existingResultIndex !== undefined &&
    existingResultIndex >= 0 &&
    existingResultIndex < ctx.accumulators.stepResults.length;

  ctx.accumulators.stepStatuses[index] = persistedStatus;

  const isSuccessLike = persistedStatus === "success" || persistedStatus === "recovered";
  if (isSuccessLike && !cleanNote && alreadyRecorded) {
    return;
  }

  ctx.accumulators.stepDisplayResults[index] = formatPersistedStepDisplay(
    description,
    persistedStatus,
    cleanNote,
  );
  setStepResultLine(
    ctx.accumulators,
    step.id,
    formatPersistedStepResult(label, description, persistedStatus, cleanNote),
  );
}

/**
 * Ensures index-addressed result accumulator arrays are long enough before
 * assigning a step result. This matters after `revise_plan` adds steps.
 *
 * @param ctx - Runtime context with shared run accumulators.
 * @param index - Step index that must be assignable.
 */
function ensureAccumulatorSlots(ctx: ExecutorToolRuntimeContext, index: number): void {
  for (let i = ctx.accumulators.stepDisplayResults.length; i <= index; i += 1) {
    const step = ctx.planState.steps[i];
    ctx.accumulators.stepDisplayResults[i] = step?.planStep.description ?? "";
  }
  for (let i = ctx.accumulators.stepStatuses.length; i <= index; i += 1) {
    const step = ctx.planState.steps[i];
    ctx.accumulators.stepStatuses[i] = step
      ? toPersistedStepStatus(step.status)
      : "queued";
  }
}

/**
 * Captured accumulator state before a plan revision, keyed by stable step id.
 */
interface AccumulatorSnapshot {
  displayById: Map<string, string>;
  statusById: Map<string, ExcelStepStatus>;
  resultLineById: Map<string, string>;
  stepNumberById: Map<string, number>;
  untrackedResultLines: string[];
}

/**
 * Captures accumulator rows by stable step id before `revise_plan` replaces
 * the plan. The accumulators are index-aligned with the current plan, so this
 * snapshot lets us rebuild them in the revised order without moving summaries
 * onto the wrong step.
 *
 * @param ctx - Runtime context with current plan and accumulators.
 * @returns Previous accumulator data keyed by step id.
 */
function captureAccumulatorState(ctx: ExecutorToolRuntimeContext): AccumulatorSnapshot {
  const displayById = new Map<string, string>();
  const statusById = new Map<string, ExcelStepStatus>();
  const resultLineById = new Map<string, string>();
  const stepNumberById = new Map<string, number>();
  const trackedResultIndexes = new Set<number>();

  ctx.planState.steps.forEach((step, index) => {
    stepNumberById.set(step.id, step.planStep.step || index + 1);

    const display = ctx.accumulators.stepDisplayResults[index];
    if (display !== undefined) {
      displayById.set(step.id, display);
    }

    const status = ctx.accumulators.stepStatuses[index];
    if (status !== undefined) {
      statusById.set(step.id, status);
    }

    const resultIndex = ctx.accumulators.stepResultIndexById.get(step.id);
    if (
      resultIndex !== undefined &&
      resultIndex >= 0 &&
      resultIndex < ctx.accumulators.stepResults.length
    ) {
      trackedResultIndexes.add(resultIndex);
      resultLineById.set(step.id, ctx.accumulators.stepResults[resultIndex]);
    }
  });

  const untrackedResultLines = ctx.accumulators.stepResults.filter(
    (_line, index) => !trackedResultIndexes.has(index),
  );

  return { displayById, statusById, resultLineById, stepNumberById, untrackedResultLines };
}

/**
 * Rebuilds index-aligned result accumulators after `revise_plan` changes the
 * step list. Reused step ids keep their prior summaries; removed ids are
 * dropped; new ids are initialized from the revised plan.
 *
 * @param ctx - Runtime context with the revised plan.
 * @param snapshot - Accumulator data captured before the revision.
 */
function realignAccumulatorsToPlan(
  ctx: ExecutorToolRuntimeContext,
  snapshot: AccumulatorSnapshot,
): void {
  const steps = ctx.planState.steps;
  ctx.accumulators.stepDisplayResults = steps.map((step) => {
    const newPersisted = toPersistedStepStatus(step.status);
    const oldStatus = snapshot.statusById.get(step.id);
    const oldStepNumber = snapshot.stepNumberById.get(step.id);
    const newStepNumber = step.planStep.step;
    if (
      oldStatus !== undefined &&
      oldStatus === newPersisted &&
      oldStepNumber === newStepNumber
    ) {
      return snapshot.displayById.get(step.id) ??
        formatPersistedStepDisplay(step.planStep.description, newPersisted, step.note);
    }
    return formatPersistedStepDisplay(step.planStep.description, newPersisted, step.note);
  });
  ctx.accumulators.stepStatuses = steps.map(
    (step) => toPersistedStepStatus(step.status),
  );

  ctx.accumulators.stepResults = [];
  ctx.accumulators.stepResultIndexById = new Map();
  for (const step of steps) {
    const line = snapshot.resultLineById.get(step.id);
    if (line === undefined) continue;
    const oldStatus = snapshot.statusById.get(step.id);
    const newPersisted = toPersistedStepStatus(step.status);
    const oldStepNumber = snapshot.stepNumberById.get(step.id);
    const newStepNumber = step.planStep.step;
    if (oldStatus === newPersisted && oldStepNumber === newStepNumber) {
      setStepResultLine(ctx.accumulators, step.id, line);
    }
  }
  ctx.accumulators.stepResults.push(...snapshot.untrackedResultLines);
}

/**
 * Formats the persisted display text shown in the final chat message.
 *
 * @param description - Original step description.
 * @param status - Persisted status to display.
 * @param note - Optional model-authored status note.
 * @returns User-facing status text for the step.
 */
function formatPersistedStepDisplay(
  description: string,
  status: ExcelStepStatus,
  note?: string,
): string {
  if (status === "success" || status === "recovered") {
    return note || description;
  }
  if (status === "paused") {
    return note ? `${description} (paused: ${note})` : `${description} (paused)`;
  }
  if (status === "warning" || status === "failed") {
    return note ? `${description} (${status}: ${note})` : `${description} (${status})`;
  }
  return description;
}

/**
 * Formats the internal result line used to build the assistant's final summary.
 *
 * @param label - User-visible step number or label.
 * @param description - Original step description.
 * @param status - Persisted status to summarize.
 * @param note - Optional model-authored status note.
 * @returns Internal result line for the final response.
 */
function formatPersistedStepResult(
  label: number,
  description: string,
  status: ExcelStepStatus,
  note?: string,
): string {
  const message = note || description;
  if (status === "paused") {
    return `${label}. paused: ${message}`;
  }
  if (status === "warning") {
    return `${label}. needs attention: ${message}`;
  }
  if (status === "failed") {
    return `${label}. failed: ${message}`;
  }
  return `${label}. ${message}`;
}

/**
 * Maps an `ExecutorStepStatus` to the persisted `ExcelStepStatus` for
 * chat history storage. The executor has richer transient states
 * (`running`, `verifying`) that collapse to `queued` at persistence
 * time since they are only meaningful during a live run.
 *
 * @param status - Live executor step status.
 * @returns The equivalent `ExcelStepStatus` for persistence.
 */
export function toPersistedStepStatus(status: ExecutorStepStatus): ExcelStepStatus {
  switch (status) {
    case "running":
    case "verifying":
      return "queued";
    case "queued":
    case "success":
    case "warning":
    case "failed":
    case "recovered":
    case "paused":
      return status;
  }
}

// ---------------------------------------------------------------------------
// Run-scoped workbook name injection
// ---------------------------------------------------------------------------

/**
 * Injects `session.pinnedWorkbookName` into raw tool input when the model
 * omits `workbookName` or passes it as null/blank. Tool schemas often require
 * a nullable `workbookName`, and the model commonly sends null to mean "use
 * the current run target"; inside the executor, the run target is the pinned
 * workbook, not whichever workbook Excel happens to have active.
 *
 * @param rawInput - Parsed LLM arguments (may be null/non-object).
 * @param session - Current run session state.
 * @returns Input with `workbookName` injected if appropriate.
 */
export function injectWorkbookName(
  rawInput: unknown,
  session: ExecutorRunSessionState,
): unknown {
  if (!session.pinnedWorkbookName) return rawInput;
  if (rawInput === null || typeof rawInput !== "object") {
    return { workbookName: session.pinnedWorkbookName };
  }
  const r = rawInput as Record<string, unknown>;
  const workbookName = r.workbookName;
  if (typeof workbookName === "string" && workbookName.trim()) return r;
  return { ...r, workbookName: session.pinnedWorkbookName };
}

/**
 * Resolves the semantic chart request for deterministic metric-basis checks.
 * Referential follow-ups can recover intent from the full live run plan,
 * while an explicit trailing/exclusion instruction in the latest request wins.
 */
function activeGraphSemanticUserRequest(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): string | undefined {
  const input =
    rawInput && typeof rawInput === "object" && !Array.isArray(rawInput)
      ? (rawInput as Record<string, unknown>)
      : {};
  const requestedStepId =
    typeof input.stepId === "string" ? input.stepId.trim() : "";
  const currentStep =
    (requestedStepId
      ? ctx.planState.steps.find((step) => step.id === requestedStepId)
      : undefined) ??
    (ctx.planState.currentStepId
      ? ctx.planState.steps.find(
          (step) => step.id === ctx.planState.currentStepId,
        )
      : undefined) ??
    ctx.planState.steps.find(
      (step) => step.status === "running" || step.status === "verifying",
    );
  const signals = resolveForwardPeIntentSignals(ctx.session.userRequest, [
    currentStep?.planStep.description,
    currentStep?.planStep.action,
    ctx.planState.summary,
    ...ctx.planState.steps.flatMap((step) => [
      step.planStep.description,
      step.planStep.action,
    ]),
  ]);
  return (
    signals.find((signal) => isExplicitForwardPeRequest(signal)) ??
    ctx.session.userRequest
  );
}

/**
 * Injects the session-lock sheet into ActiveGraph creation calls when the model
 * omitted an explicit target sheet.
 */
function injectActiveGraphTargetSheet(
  rawInput: unknown,
  session: ExecutorRunSessionState,
): unknown {
  const withWorkbookName = injectWorkbookName(rawInput, session);
  if (withWorkbookName === null || typeof withWorkbookName !== "object") {
    return withWorkbookName;
  }

  const r = withWorkbookName as Record<string, unknown>;
  if (r.operation !== "create_active_graph" || !session.sessionLockSheet.trim()) {
    return withWorkbookName;
  }

  const target =
    r.target && typeof r.target === "object" && !Array.isArray(r.target)
      ? (r.target as Record<string, unknown>)
      : {};
  if ("sheetRef" in target) {
    return withWorkbookName;
  }

  return {
    ...r,
    target: {
      ...target,
      sheetRef: { name: session.sessionLockSheet },
    },
  };
}

// ---------------------------------------------------------------------------
// Input normalisers
// ---------------------------------------------------------------------------

/**
 * Normalises raw LLM arguments for `set_step_status`.
 *
 * @param raw - Parsed JSON from the model.
 * @returns Normalised input with safe defaults.
 */
function normaliseSetStepStatusInput(raw: unknown): SetStepStatusInput {
  if (raw === null || typeof raw !== "object") {
    return { stepId: "", status: "queued", note: null };
  }
  const r = raw as Record<string, unknown>;
  return {
    stepId: typeof r.stepId === "string" ? r.stepId : "",
    status: (typeof r.status === "string" ? r.status : "queued") as ExecutorStepStatus,
    note: typeof r.note === "string" ? r.note : null,
  };
}

/**
 * Normalises raw LLM arguments for `set_run_plan`.
 *
 * @param raw - Parsed JSON from the model.
 * @returns Normalised input with safe defaults.
 */
function normaliseSetRunPlanInput(raw: unknown): SetRunPlanInput {
  if (raw === null || typeof raw !== "object") {
    return { summary: "", estimatedComplexity: "moderate", steps: [] };
  }
  const r = raw as Record<string, unknown>;
  const complexity = r.estimatedComplexity;
  const estimatedComplexity =
    complexity === "simple" || complexity === "complex" || complexity === "moderate"
      ? complexity
      : "moderate";
  const rawSteps = Array.isArray(r.steps) ? r.steps : [];
  return {
    summary: typeof r.summary === "string" ? r.summary : "",
    estimatedComplexity,
    steps: rawSteps.map((step, index) => {
      const s = step && typeof step === "object" ? step as Record<string, unknown> : {};
      return {
        id: typeof s.id === "string" ? s.id : `step_${index + 1}`,
        step: typeof s.step === "number" && Number.isInteger(s.step) ? s.step : index + 1,
        description: typeof s.description === "string" ? s.description : "",
        action: typeof s.action === "string" ? s.action : "edit",
        targetSheet: typeof s.targetSheet === "string" ? s.targetSheet : null,
      };
    }),
  };
}

/**
 * Normalises raw LLM arguments for `revise_plan`.
 *
 * @param raw - Parsed JSON from the model.
 * @returns Normalised input with safe defaults.
 */
function normaliseRevisePlanInput(raw: unknown): RevisePlanInput {
  if (raw === null || typeof raw !== "object") {
    return { summary: "", steps: [] };
  }
  const r = raw as Record<string, unknown>;
  const summary = typeof r.summary === "string" ? r.summary : "";
  const rawSteps = Array.isArray(r.steps) ? r.steps : [];

  const steps: RevisePlanStepInput[] = rawSteps.map((s: unknown) => {
    if (s === null || typeof s !== "object") {
      return {
        id: "",
        step: 0,
        description: "",
        action: "",
        targetSheet: null,
        applyTemplate: null,
        status: "queued" as ExecutorStepStatus,
        note: null,
      };
    }
    const entry = s as Record<string, unknown>;
    const rawApplyTemplate = entry.applyTemplate;
    const applyTemplate =
      rawApplyTemplate !== null && typeof rawApplyTemplate === "object"
        ? normaliseApplyTemplate(rawApplyTemplate)
        : null;
    return {
      id: typeof entry.id === "string" ? entry.id : "",
      step: typeof entry.step === "number" ? entry.step : 0,
      description: typeof entry.description === "string" ? entry.description : "",
      action: typeof entry.action === "string" ? entry.action : "",
      targetSheet: typeof entry.targetSheet === "string" ? entry.targetSheet : null,
      applyTemplate,
      status: (typeof entry.status === "string" ? entry.status : "queued") as ExecutorStepStatus,
      note: typeof entry.note === "string" ? entry.note : null,
    };
  });

  return { summary, steps };
}

/**
 * Normalises the optional `applyTemplate` payload inside a revised plan
 * step. Missing optional fields become null so the executor can map
 * them back to `undefined` for `PlanStep.applyTemplate`.
 *
 * @param raw - Parsed JSON object from a `revise_plan` step.
 * @returns Normalised template metadata.
 */
function normaliseApplyTemplate(raw: object): NonNullable<RevisePlanStepInput["applyTemplate"]> {
  const r = raw as Record<string, unknown>;
  return {
    templateKey: typeof r.templateKey === "string" ? r.templateKey : "",
    companyIdentifier: typeof r.companyIdentifier === "string" ? r.companyIdentifier : null,
    targetWorkbook:
      r.targetWorkbook === "current" || r.targetWorkbook === "new"
        ? r.targetWorkbook
        : null,
  };
}
